import { Download, FileText } from "lucide-react";
import { useLang } from "../../i18n";

const PDFS = {
  "gamme-preformes": [
    { href: "/assets/fiches/fiche-preformes-pet.pdf", labelKey: "pdfLabel" },
    { href: "/assets/fiches/fiche-preforme-28-3g.pdf", labelKey: "pdfLabel28" },
  ],
  "gamme-bouchons": [{ href: "/assets/fiches/fiche-bouchon-30-25.pdf", labelKey: "pdfLabel" }],
  "gamme-film": [{ href: "/assets/fiches/fiche-film-retractable.pdf", labelKey: "pdfLabel" }],
  "gamme-gobelets": [{ href: "/assets/fiches/fiche-gobelets-carton.pdf", labelKey: "pdfLabel" }],
};
import { PANEL_IMAGES } from "../../utils";
import { Reveal } from "./Section";

const Ranges = () => {
  const { t } = useLang();
  return (
    <section data-testid="ranges-section">
      {t.ranges.map((r, i) => (
        <div key={r.id} id={r.id} className="grid lg:grid-cols-2 border-b border-[#232733] scroll-mt-24">
          <div className={`relative min-h-[320px] lg:min-h-0 overflow-hidden ${i % 2 === 1 ? "lg:order-2" : ""}`}>
            <div className="lg:sticky lg:top-24 h-full">
              <img
                src={PANEL_IMAGES[i]}
                alt={r.name}
                loading="lazy"
                decoding="async"
                className={`absolute inset-0 w-full h-full transition-all duration-700 ${i === 0 ? "object-contain bg-[#f4f4f3]" : "object-cover"}`}
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#0A0B0E]/40 to-transparent" />
              <span className="absolute top-6 left-6 font-mono text-[10px] uppercase tracking-[0.3em] text-white bg-[#0A0B0E]/70 backdrop-blur px-3 py-1.5 border border-[#232733]">
                Gamme {r.index}
              </span>
            </div>
          </div>
          <Reveal className="px-6 lg:px-14 py-16 lg:py-24 bg-[#0A0B0E]">
            <span className="font-mono text-xs text-[#0055FF] tracking-[0.3em]" data-testid={`range-index-${i}`}>
              GAMME {r.index}
            </span>
            <h3 className="font-display text-3xl lg:text-5xl font-medium tracking-tighter text-white mt-4">{r.name}</h3>
            <p className="text-base text-[#A0AABF] mt-4 leading-relaxed">{r.desc}</p>
            <div className="mt-10 border-t border-[#232733]" data-testid={`range-table-${i}`}>
              {r.rows.map((row, j) => (
                <div key={j} className="spec-row grid grid-cols-[140px_1fr] sm:grid-cols-[180px_1fr] border-b border-[#232733]">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#647087] py-4 pr-4">{row.k}</span>
                  <span className="font-mono text-xs sm:text-sm text-white py-4">{row.v}</span>
                </div>
              ))}
            </div>
            {r.grammage && (
              <div className="mt-10" data-testid={`range-grammage-${i}`}>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#647087] mb-3">{r.grammage.title}</div>
                <div className="border border-[#232733] overflow-x-auto">
                  <table className="w-full font-mono text-[11px] sm:text-xs">
                    <thead>
                      <tr className="bg-[#111318] text-[#647087] uppercase tracking-wider text-left">
                        {r.grammage.head.map((h, j) => (
                          <th key={j} className="px-3 py-2.5 font-medium border-b border-[#232733] whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {r.grammage.rows.map((row, j) => (
                        <tr key={j} className="spec-row text-white border-b border-[#232733] last:border-b-0">
                          {row.map((cell, k) => (
                            <td key={k} className={`px-3 py-2.5 whitespace-nowrap ${k === 0 ? "text-[#4D8BFF]" : ""}`}>{cell}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {r.plans && (
              <div className={`grid gap-3 mt-8 ${r.plans.length > 1 ? "grid-cols-2 sm:grid-cols-3" : ""}`} data-testid={`range-plans-${i}`}>
                {r.plans.map((p, j) => (
                  <figure key={j} className="border border-[#232733] bg-[#0A0B0E] group/p overflow-hidden">
                    <img
                      src={p.src}
                      alt={p.caption}
                      loading="lazy"
                      decoding="async"
                      className={`w-full opacity-90 group-hover/p:opacity-100 transition-opacity ${r.plans.length > 1 ? "h-40 object-cover object-top" : "h-64 object-contain"}`}
                    />
                    <figcaption className="font-mono text-[9px] uppercase tracking-[0.15em] text-[#647087] px-3 py-2 border-t border-[#232733]">{p.caption}</figcaption>
                  </figure>
                ))}
              </div>
            )}
            {r.colors && (
              <div className="flex flex-wrap gap-3 mt-8" data-testid="cap-colors">
                {r.colors.map((c, j) => (
                  <div key={j} className="flex flex-col items-center gap-2 group/c" title={c.name}>
                    <span
                      className="w-9 h-9 border border-[#232733] transition-transform group-hover/c:scale-110"
                      style={{ backgroundColor: c.hex }}
                    />
                    <span className="font-mono text-[9px] uppercase tracking-wider text-[#647087]">{c.name}</span>
                  </div>
                ))}
                <div className="flex flex-col items-center gap-2">
                  <span className="w-9 h-9 border border-dashed border-[#4D8BFF] bg-gradient-to-br from-[#0055FF]/30 to-[#FF4D00]/30" />
                  <span className="font-mono text-[9px] uppercase tracking-wider text-[#4D8BFF]">{r.customColor}</span>
                </div>
              </div>
            )}
            {r.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-8">
                {r.tags.map((tag, j) => (
                  <span key={j} className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#A0AABF] border border-[#232733] px-3 py-1.5">
                    {tag}
                  </span>
                ))}
              </div>
            )}
            {r.drawings && (
              <div className="mt-10" data-testid={`range-drawings-${i}`}>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#647087] mb-3">{r.plansTitle}</div>
                <div className="grid sm:grid-cols-2 gap-2">
                  {r.drawings.map((d, j) => (
                    <a
                      key={j}
                      href={d.href}
                      download
                      data-testid={`range-drawing-${i}-${j}`}
                      className="group/f flex items-center gap-3 border border-[#232733] px-4 py-3 font-mono text-[11px] uppercase tracking-[0.15em] text-[#A0AABF] hover:border-[#0055FF] hover:text-white transition-colors"
                    >
                      <FileText size={14} className="text-[#0055FF] shrink-0" />
                      {d.label}
                    </a>
                  ))}
                </div>
              </div>
            )}
            <div className="flex flex-wrap gap-3 mt-10">
              {PDFS[r.id].map((p, j) => (
                <a
                  key={j}
                  href={p.href}
                  download
                  data-testid={`range-pdf-${i}-${j}`}
                  className="group/d inline-flex items-center gap-3 border border-[#0055FF] text-[#4D8BFF] font-mono text-[11px] uppercase tracking-[0.2em] px-6 py-3.5 hover:bg-[#0055FF] hover:text-white transition-colors"
                >
                  <Download size={14} className="transition-transform group-hover/d:translate-y-0.5" />
                  {t.products[p.labelKey]}
                </a>
              ))}
            </div>
          </Reveal>
        </div>
      ))}
    </section>
  );
};

export default Ranges;
